import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center whitespace-nowrap border font-semibold leading-none rounded-full transition-colors',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground shadow',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        destructive: 'border-transparent bg-destructive text-destructive-foreground shadow',
        outline: 'text-foreground',
        success: 'border-success-border bg-success text-success-foreground',
        // Softer, Figma-like published badge
        published: 'border-[#b8ddd4] bg-[#d5eee9] text-[#055135]',
        // Neutral draft badge
        draft: 'border-[#e5e5e5] bg-[#f5f5f5] text-[#8d8c8f]',
        active: 'border-success-border bg-success text-success-foreground',
        inactive: 'border-border bg-muted text-muted-foreground',
      },
      size: {
        default: 'h-6 px-3 text-xs rounded-[8px]',
        sm: 'h-5 px-2 text-[11px] rounded-[6px]',
        lg: 'h-7 px-4 text-sm rounded-[8px]',
        // Figma-like chip: 28px height, 12px horizontal padding, 13px text, full pill radius
        pill: 'h-7 px-[16px] text-[13px] rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant, size }), className)} {...props} />;
}

export { Badge, badgeVariants };
